/**
 * The web half of the shared navigation manifest's icon layer.
 *
 * `nav.config.ts` names an icon rather than importing one, so the same list can
 * be read by a React app and a React Native app. This is where web turns a name
 * back into a component; mobile has the equivalent in its `Glyph` registry, and
 * the settings nav has used this shape for a while.
 *
 * Typed as an exhaustive `Record`, so adding a name to `NavIconName` without a
 * glyph here is a type error rather than a blank square.
 */

import {
  AdjustmentsHorizontalIcon,
  ArchiveBoxIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  BoltIcon,
  CalendarIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  FireIcon,
  FlagIcon,
  HomeIcon,
  NewspaperIcon,
  QuestionMarkCircleIcon,
  ScaleIcon,
  TicketIcon,
  TrophyIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

import type { HeroIcon } from './navIcons.types';
import type { NavIconName } from '@personal-trainer-app/shared/src/config/nav.config';

/** Name → heroicons v2 outline component. */
export const NAV_ICONS: Record<NavIconName, HeroIcon> = {
  adjustmentsHorizontal: AdjustmentsHorizontalIcon,
  archiveBox: ArchiveBoxIcon,
  arrowRightOnRectangle: ArrowRightOnRectangleIcon,
  bell: BellIcon,
  bolt: BoltIcon,
  calendar: CalendarIcon,
  chartBar: ChartBarIcon,
  clipboardDocumentList: ClipboardDocumentListIcon,
  cog6Tooth: Cog6ToothIcon,
  fire: FireIcon,
  flag: FlagIcon,
  home: HomeIcon,
  newspaper: NewspaperIcon,
  questionMarkCircle: QuestionMarkCircleIcon,
  scale: ScaleIcon,
  ticket: TicketIcon,
  trophy: TrophyIcon,
  user: UserIcon,
  userGroup: UserGroupIcon,
  users: UsersIcon,
};

/**
 * Render a manifest icon at a given size.
 *
 * @example
 *   navIcon('home', 'h-5 w-5')   // sidebar
 *   navIcon('bell', 'h-full w-full') // fills the menu item's 18px box
 */
export function navIcon(
  name: NavIconName,
  className: string
): React.JSX.Element {
  const Icon = NAV_ICONS[name];
  return <Icon className={className} />;
}
